import React, { useState, useEffect } from "react";
import {
	doc,
	getDoc,
	setDoc,
	collection,
	addDoc,
	query,
	orderBy,
	onSnapshot,
	serverTimestamp,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import {
	Settings,
	Save,
	CheckCircle,
	History,
	ChevronDown,
	ChevronUp,
	Calendar,
} from "lucide-react";
import ScheduleManager from "./ScheduleManager";

const GymSettings = ({ loading: parentLoading }) => {
	const [activeSettingsTab, setActiveSettingsTab] = useState("settings");
	const [gymCode, setGymCode] = useState("");
	const [memberCap, setMemberCap] = useState("");
	const [codeUsageCount, setCodeUsageCount] = useState(0);
	const [saving, setSaving] = useState(false);
	const [loadingSettings, setLoadingSettings] = useState(true);
	const [success, setSuccess] = useState("");
	const [error, setError] = useState("");
	const [codeHistory, setCodeHistory] = useState([]);
	const [showHistory, setShowHistory] = useState(false);

	// Fetch current settings
	useEffect(() => {
		const fetchSettings = async () => {
			setLoadingSettings(true);
			try {
				const settingsDoc = await getDoc(doc(db, "config", "gymSettings"));
				if (settingsDoc.exists()) {
					const data = settingsDoc.data();
					setGymCode(data.gymCode || "");
					setMemberCap(data.memberCap ?? "");
					setCodeUsageCount(data.codeUsageCount || 0);
				}
			} catch (err) {
				setError("Failed to load settings.");
			}
			setLoadingSettings(false);
		};
		fetchSettings();
	}, []);

	// Listen for access code history
	useEffect(() => {
		const q = query(
			collection(db, "accessCodeHistory"),
			orderBy("archivedAt", "desc"),
		);
		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const history = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
					archivedAt:
						doc.data().archivedAt?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}));
				setCodeHistory(history);
			},
			(error) => {
				console.error("Error listening to code history:", error);
			},
		);
		return unsubscribe;
	}, []);

	const handleSave = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");

		if (!gymCode.trim()) {
			setError("Access code is required.");
			return;
		}
		const cap = parseInt(memberCap, 10);
		if (isNaN(cap) || cap < 1) {
			setError("Member cap must be a positive number.");
			return;
		}

		setSaving(true);
		try {
			// Get current settings to check if code changed
			const currentDoc = await getDoc(doc(db, "config", "gymSettings"));
			const currentData = currentDoc.exists() ? currentDoc.data() : {};
			const codeChanged =
				currentData.gymCode && currentData.gymCode !== gymCode.trim();

			// If the access code changed, archive the old one
			if (codeChanged) {
				await addDoc(collection(db, "accessCodeHistory"), {
					code: currentData.gymCode,
					memberCap: currentData.memberCap || 0,
					usageCount: currentData.codeUsageCount || 0,
					archivedAt: serverTimestamp(),
				});
			}

			await setDoc(
				doc(db, "config", "gymSettings"),
				{
					gymCode: gymCode.trim(),
					memberCap: cap,
					// Reset usage count if code changed, keep it if only cap changed
					codeUsageCount: codeChanged ? 0 : currentData.codeUsageCount || 0,
				},
				{ merge: true },
			);

			setCodeUsageCount(codeChanged ? 0 : currentData.codeUsageCount || 0);
			setSuccess("Settings saved successfully.");
			setTimeout(() => setSuccess(""), 3000);
		} catch (err) {
			setError("Failed to save settings. Please try again.");
		}
		setSaving(false);
	};

	if (loadingSettings) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="card">
			<div className="card-header">
				<h5 className="mb-0">
					<Settings className="me-2" size={20} />
					Gym Settings
				</h5>
				{/* Tabs */}
				<ul
					className="nav nav-tabs mt-3"
					style={{ flexWrap: "nowrap", borderBottom: "none", marginBottom: 0 }}
				>
					<li className="nav-item" style={{ flex: "1 1 0" }}>
						<button
							className={`nav-link ${activeSettingsTab === "settings" ? "active" : ""} w-100 text-center`}
							onClick={() => setActiveSettingsTab("settings")}
							style={
								activeSettingsTab === "settings"
									? {
											backgroundColor: "#d15a36",
											borderColor: "#d15a36",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
										}
									: {
											backgroundColor: "rgba(209, 90, 54, 0.15)",
											borderColor: "transparent",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
										}
							}
						>
							<Settings size={14} className="me-1" />
							Settings
						</button>
					</li>
					<li className="nav-item" style={{ flex: "1 1 0" }}>
						<button
							className={`nav-link ${activeSettingsTab === "schedule" ? "active" : ""} w-100 text-center`}
							onClick={() => setActiveSettingsTab("schedule")}
							style={
								activeSettingsTab === "schedule"
									? {
											backgroundColor: "#d15a36",
											borderColor: "#d15a36",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
										}
									: {
											backgroundColor: "rgba(209, 90, 54, 0.15)",
											borderColor: "transparent",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
										}
							}
						>
							<Calendar size={14} className="me-1" />
							Schedule
						</button>
					</li>
				</ul>
			</div>
			<div className="card-body">
				{/* Schedule Tab Content */}
				{activeSettingsTab === "schedule" && <ScheduleManager />}

				{/* Settings Tab Content */}
				{activeSettingsTab === "settings" && (
					<>
						<p className="text-muted mb-4">
							Configure the access code and member cap for self-registration.
						</p>

						{error && (
							<div className="alert alert-danger" role="alert">
								{error}
							</div>
						)}
						{success && (
							<div
								className="alert alert-success d-flex align-items-center"
								role="alert"
							>
								<CheckCircle size={18} className="me-2" />
								{success}
							</div>
						)}

						<form onSubmit={handleSave}>
							<div className="row g-3">
								<div className="col-md-6">
									<label className="form-label">Access Code</label>
									<input
										type="text"
										className="form-control"
										value={gymCode}
										onChange={(e) => setGymCode(e.target.value)}
										placeholder="Enter gym access code"
										disabled={saving}
									/>
									<small className="text-muted">
										Users must enter this code to register.
									</small>
								</div>
								<div className="col-md-4">
									<label className="form-label">Member Cap</label>
									<input
										type="number"
										className="form-control"
										value={memberCap}
										onChange={(e) => setMemberCap(e.target.value)}
										placeholder="Max number of members"
										min="1"
										disabled={saving}
									/>
									<small className="text-muted">
										Maximum number of non-trainer members allowed.
									</small>
								</div>
								<div className="col-md-2">
									<label className="form-label">Used</label>
									<div
										className="form-control bg-light text-center"
										style={{ cursor: "default" }}
									>
										{codeUsageCount}
									</div>
									<small className="text-muted">Times code used</small>
								</div>
								<div className="col-12">
									<button
										type="submit"
										className="btn btn-primary"
										disabled={saving}
									>
										{saving ? (
											<>Saving...</>
										) : (
											<>
												<Save size={16} className="me-1" />
												Save Settings
											</>
										)}
									</button>
								</div>
							</div>
						</form>

						{/* Access Code History */}
						<div className="mt-4">
							<button
								className="btn btn-sm btn-outline-secondary"
								onClick={() => setShowHistory(!showHistory)}
							>
								<History size={14} className="me-1" />
								{showHistory ? "Hide" : "View"} Code History
								{codeHistory.length > 0 && ` (${codeHistory.length})`}
								{showHistory ? (
									<ChevronUp size={14} className="ms-1" />
								) : (
									<ChevronDown size={14} className="ms-1" />
								)}
							</button>

							{showHistory && (
								<div className="mt-3">
									{codeHistory.length === 0 ? (
										<p className="text-muted">
											No previous access codes. History will appear here when
											you change the access code.
										</p>
									) : (
										<div className="table-responsive">
											<table className="table table-sm table-hover">
												<thead>
													<tr>
														<th>Access Code</th>
														<th>Member Cap</th>
														<th>Times Used</th>
														<th>Retired</th>
													</tr>
												</thead>
												<tbody>
													{codeHistory.map((entry) => (
														<tr key={entry.id}>
															<td>
																<code>{entry.code}</code>
															</td>
															<td>{entry.memberCap}</td>
															<td>{entry.usageCount}</td>
															<td>
																{new Date(
																	entry.archivedAt,
																).toLocaleDateString()}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default GymSettings;
